import { yupResolver } from "@hookform/resolvers/yup";
import { Box, Grid, Stack, Typography } from "@mui/material";
import { useQueryClient } from "@tanstack/react-query";
import { FormRadioGroup } from "@zfp-akademie-a-s/zfp-design/form";
import { FormNumberField } from "@zfp-akademie-a-s/zfp-design/form/number-field";
import { AnalysisDetailContext } from "Components/Analyses/Detail/AnalysisDetailContext";
import { type DisabilityIncomeFormType, getDisabilityIncomeFormSchema } from "Forms/Schemas/Budget/Incomes/disabilityIncomeFormSchema";
import { toRadioOptions } from "Forms/fieldOptions";

import {
  getGetBudgetOverviewQueryKey,
  getListIncomesQueryKey,
  useCreateIncome,
  useDeleteIncome,
  useGetIncome,
  useUpdateIncome,
} from "Infrastructure/ApiZfp/AnalyzerApi";
import { AmountFrequency, type Income, IncomeAmountAmountType, IncomeBodyIncomeType } from "Infrastructure/ApiZfp/Schema";
import { Spacing } from "Infrastructure/Themes/Themes";
import { Resources, useResource } from "Infrastructure/Translations/Resources";
import { forwardRef, useContext, useEffect, useImperativeHandle } from "react";
import { useForm } from "react-hook-form";
import { toast } from "sonner";

type Props = {
  onClose: () => void;
  clientId?: string;
  edit?: Income;
};

export const DisabilityIncomeForm = forwardRef<{}, Props>(({ onClose, clientId, edit }, ref) => {
  const { readOnly, analysisId, versionId } = useContext(AnalysisDetailContext);
  const { t } = useResource();
  const queryClient = useQueryClient();
  const { data: editIncomeData } = useGetIncome(analysisId, versionId, edit?.guid ?? "", {
    query: { enabled: !!edit },
  });
  const { mutate: putIncome } = useUpdateIncome();
  const { mutate } = useCreateIncome();
  const { mutate: deleteIncome } = useDeleteIncome();

  const { control, handleSubmit, setValue, watch, clearErrors } = useForm<DisabilityIncomeFormType>({
    resolver: yupResolver(getDisabilityIncomeFormSchema(t)),
    mode: "onBlur",
    defaultValues: {
      degree: IncomeAmountAmountType.first_degree_disability,
      amountValueFirstDegree: "" as any,
      amountValueSecondDegree: "" as any,
      amountValueThirdDegree: "" as any,
    },
  });

  useEffect(() => {
    if (editIncomeData) {
      if (editIncomeData?.incomeAmounts.some((x) => x.amountType === IncomeAmountAmountType.first_degree_disability)) {
        setValue("degree", IncomeAmountAmountType.first_degree_disability);
        setValue("amountValueFirstDegree", (editIncomeData?.incomeAmounts[0]?.amount.value.toString() ?? "") as any);
        setValue("amountValueSecondDegree", "" as any);
        setValue("amountValueThirdDegree", "" as any);
      }

      if (editIncomeData?.incomeAmounts.some((x) => x.amountType === IncomeAmountAmountType.second_degree_disability)) {
        setValue("degree", IncomeAmountAmountType.second_degree_disability);
        setValue("amountValueFirstDegree", "" as any);
        setValue("amountValueSecondDegree", (editIncomeData?.incomeAmounts[0]?.amount.value.toString() ?? "") as any);
        setValue("amountValueThirdDegree", "" as any);
      }

      if (editIncomeData?.incomeAmounts.some((x) => x.amountType === IncomeAmountAmountType.third_degree_disability)) {
        setValue("degree", IncomeAmountAmountType.third_degree_disability);
        setValue("amountValueFirstDegree", "" as any);
        setValue("amountValueSecondDegree", "" as any);
        setValue("amountValueThirdDegree", (editIncomeData?.incomeAmounts[0]?.amount.value.toString() ?? "") as any);
      }
    }
  }, [editIncomeData, setValue]);

  const selectedDegree = watch("degree");

  useImperativeHandle(ref, () => ({
    submitForm: () => {
      handleSubmit(onSubmit)();
    },
    deleteIncome: () => {
      handleDelete();
    },
  }));

  const onSubmit = async (data: DisabilityIncomeFormType) => {
    const amountValue =
      data.degree === IncomeAmountAmountType.first_degree_disability
        ? data.amountValueFirstDegree
        : data.degree === IncomeAmountAmountType.second_degree_disability
          ? data.amountValueSecondDegree
          : data.amountValueThirdDegree;

    if (edit) {
      putIncome(
        {
          analysisId,
          versionId,
          incomeId: edit.guid,
          data: {
            incomeType: IncomeBodyIncomeType.disability,
            clientGuid: clientId ?? "",
            incomeAmounts: [
              {
                amountType: data.degree,
                amount: {
                  value: Number(amountValue) || 0,
                  currencyCode: "CZK",
                  frequency: AmountFrequency.monthly,
                },
                name: "",
              },
            ],
          },
        },
        {
          onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: getListIncomesQueryKey(analysisId, versionId) });
            queryClient.invalidateQueries({ queryKey: getGetBudgetOverviewQueryKey(analysisId, versionId) });
            onClose();
          },
          onError: () => {
            toast.error(t(Resources.AnalysisDetail.Budget.Incomes.Form.Errors.IncomeSubmitError));
          },
        },
      );
    } else {
      mutate(
        {
          data: {
            incomeType: IncomeBodyIncomeType.disability,
            clientGuid: clientId ?? "",
            incomeAmounts: [
              {
                amountType: data.degree,
                amount: {
                  value: Number(amountValue) || 0,
                  currencyCode: "CZK",
                  frequency: AmountFrequency.monthly,
                },
                name: "",
              },
            ],
          },
          analysisId,
          versionId,
        },
        {
          onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: getListIncomesQueryKey(analysisId, versionId) });
            queryClient.invalidateQueries({ queryKey: getGetBudgetOverviewQueryKey(analysisId, versionId) });
            onClose();
          },
          onError: () => {
            toast.error(t(Resources.AnalysisDetail.Budget.Incomes.Form.Errors.IncomeSubmitError));
          },
        },
      );
    }
  };

  const handleDelete = () => {
    deleteIncome(
      {
        analysisId,
        versionId,
        incomeId: edit?.guid ?? "",
      },
      {
        onSuccess: () => {
          queryClient.invalidateQueries({ queryKey: getListIncomesQueryKey(analysisId, versionId) });
          queryClient.invalidateQueries({ queryKey: getGetBudgetOverviewQueryKey(analysisId, versionId) });
          onClose();
        },
        onError: () => {
          toast.error(t(Resources.AnalysisDetail.Budget.Incomes.Form.Errors.IncomeSubmitError));
        },
      },
    );
  };

  return (
    <Stack spacing={Spacing.XXL} pb={Spacing.XXL} sx={{ height: "100%" }}>
      <Box>
        <Typography fontWeight={600} variant="body1" mb={Spacing.XL}>
          {t(Resources.AnalysisDetail.Budget.Incomes.IncomeTypes.Disability)}
        </Typography>

        <Grid container spacing={Spacing.XL}>
          <Grid size={12}>
            <FormRadioGroup
              control={control}
              name="degree"
              options={toRadioOptions([
                {
                  code: IncomeAmountAmountType.first_degree_disability,
                  name: t(Resources.AnalysisDetail.Budget.Incomes.Form.Fields.DisabilityIncome.FirstDegree),
                },
              ])}
              defaultValue={IncomeAmountAmountType.first_degree_disability}
              columns={2}
              disabled={readOnly}
              onChange={() => {
                setValue("amountValueSecondDegree", "" as any);
                setValue("amountValueThirdDegree", "" as any);
                clearErrors(["amountValueSecondDegree", "amountValueThirdDegree"]);
              }}
            />

            <FormNumberField
              control={control}
              name="amountValueFirstDegree"
              required
              valueType="text"
              scale={2}
              min={0}
              thousandsSeparator=" "
              unit={`${t(Resources.Common.CZKSuffix)}/${t(Resources.Common.Month)}`}
              disabled={selectedDegree !== IncomeAmountAmountType.first_degree_disability || readOnly}
              placeholder="0"
              sx={{
                width: "50%",
              }}
              fullWidth
            />

            <FormRadioGroup
              control={control}
              name="degree"
              options={toRadioOptions([
                {
                  code: IncomeAmountAmountType.second_degree_disability,
                  name: t(Resources.AnalysisDetail.Budget.Incomes.Form.Fields.DisabilityIncome.SecondDegree),
                },
              ])}
              defaultValue={IncomeAmountAmountType.second_degree_disability}
              columns={2}
              disabled={readOnly}
              onChange={() => {
                setValue("amountValueFirstDegree", "" as any);
                setValue("amountValueThirdDegree", "" as any);
                clearErrors(["amountValueFirstDegree", "amountValueThirdDegree"]);
              }}
            />

            <FormNumberField
              control={control}
              name="amountValueSecondDegree"
              required
              valueType="text"
              scale={2}
              min={0}
              thousandsSeparator=" "
              unit={`${t(Resources.Common.CZKSuffix)}/${t(Resources.Common.Month)}`}
              disabled={selectedDegree !== IncomeAmountAmountType.second_degree_disability || readOnly}
              placeholder="0"
              sx={{
                width: "50%",
              }}
              fullWidth
            />

            <FormRadioGroup
              control={control}
              name="degree"
              options={toRadioOptions([
                {
                  code: IncomeAmountAmountType.third_degree_disability,
                  name: t(Resources.AnalysisDetail.Budget.Incomes.Form.Fields.DisabilityIncome.ThirdDegree),
                },
              ])}
              defaultValue={IncomeAmountAmountType.third_degree_disability}
              columns={2}
              disabled={readOnly}
              onChange={() => {
                setValue("amountValueFirstDegree", "" as any);
                setValue("amountValueSecondDegree", "" as any);
                clearErrors(["amountValueFirstDegree", "amountValueSecondDegree"]);
              }}
            />

            <FormNumberField
              control={control}
              name="amountValueThirdDegree"
              required
              valueType="text"
              scale={2}
              min={0}
              thousandsSeparator=" "
              unit={`${t(Resources.Common.CZKSuffix)}/${t(Resources.Common.Month)}`}
              disabled={selectedDegree !== IncomeAmountAmountType.third_degree_disability || readOnly}
              placeholder="0"
              sx={{
                width: "50%",
              }}
              fullWidth
            />
          </Grid>
        </Grid>
      </Box>
    </Stack>
  );
});
